import type { FC } from 'react';
import { Button, Spinner } from '@selfinspectionorg/ui-kit';
import colors from '@selfinspectionorg/ui-kit/css/colors.module.scss';
import styles from './mfa.module.scss';
import { PasskeySection } from './PasskeySection';
import { AuthenticatorSection } from './AuthenticatorSection';
import { EmailSection } from './EmailSection';
import { useMfaStatus } from '@/shared/api/mfa';

/**
 * The MFA half of the Profile Security tab: loads the account's MFA status and renders the Passkey,
 * Authenticator, and Email sections stacked in a single column with dividers between them. `mutate` is
 * threaded into each section so add/remove/setup/disable flows revalidate the shared status in place.
 */
export const MfaSections: FC = () => {
  const { status, isLoading, error, mutate } = useMfaStatus();

  if (isLoading) {
    return (
      <div className={styles.state} data-testid="mfa-loading">
        <Spinner color={colors.selfInspection_600} width={40} height={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.state} data-testid="mfa-error">
        <p className={styles.stateText}>We couldn&apos;t load your security settings.</p>
        <Button onClick={() => mutate()} data-testid="mfa-retry-button">
          Try again
        </Button>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  return (
    <>
      <PasskeySection passkeys={status.passkeys} mutate={mutate} />
      <hr className={styles.divider} />
      <AuthenticatorSection hasTotp={status.hasTotp} hasEmail={status.hasEmail} mutate={mutate} />
      <hr className={styles.divider} />
      <EmailSection
        hasEmail={status.hasEmail}
        emailAvailable={status.emailAvailable}
        hasTotp={status.hasTotp}
        mutate={mutate}
      />
    </>
  );
};
